import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { recruitmentTheme, themeClasses } from '../styles/theme';
import { ChangePasswordModal } from '../components/modals/ChangePasswordModal';
import { Settings, User, Shield, Bell, Users, Plus, Edit, Trash2, X, Save, UserCheck, Download, Upload } from 'lucide-react';
import { StatusBadge } from '../components/ui/StatusBadge';
import { addToRecycleBin } from '../utils/recycleBin';
import { 
  subscribeToUsers, 
  addUser, 
  updateUser, 
  softDeleteUser, 
  migrateStandardUsers,
  User as FirebaseUser
} from '../services/userService';

interface UserManagement {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  lastLogin: string;
  permissions: string[];
  phone?: string;
  department?: string;
  notes?: string;
}

const SettingsPage: React.FC = () => {
  const [firebaseUsersLoading, setFirebaseUsersLoading] = useState(true);
  const { user, updateProfile } = useAuth();
  const { settings, updateNotificationSettings, updateAppSettings } = useSettings();
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  
  // Track changes for auto-save functionality
  const [tempSettings, setTempSettings] = useState({
    applicationName: settings.app.applicationName,
    defaultUserRole: settings.app.defaultUserRole,
    dataRetention: '365',
    defaultCountry: 'Kenya',
    defaultCurrency: 'KES',
    autoLogout: '30'
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // User Management state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserManagement | null>(null);
  const [users, setUsers] = useState<UserManagement[]>([]);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    role: 'Broker',
    status: 'Active',
    permissions: [] as string[],
    phone: '',
    department: '',
    notes: ''
  });

  const availablePermissions = [
    { value: 'candidates', label: 'Candidates' },
    { value: 'jobs', label: 'Jobs' },
    { value: 'tasks', label: 'Tasks' },
    { value: 'appointments', label: 'Appointments' },
    { value: 'reports', label: 'Reports' },
    { value: 'settings', label: 'Settings' }
  ];

  const userRoleOptions = [
    { value: 'admin', label: 'Admin', color: 'text-purple-700', bgColor: 'bg-purple-100' },
    { value: 'broker', label: 'Broker', color: 'text-blue-700', bgColor: 'bg-blue-100' },
    { value: 'receptionist', label: 'Receptionist', color: 'text-green-700', bgColor: 'bg-green-100' }
  ];

  const userStatusOptions = [
    { value: 'active', label: 'Active', color: 'text-green-700', bgColor: 'bg-green-100' },
    { value: 'inactive', label: 'Inactive', color: 'text-red-700', bgColor: 'bg-red-100' },
    { value: 'pending', label: 'Pending', color: 'text-yellow-700', bgColor: 'bg-yellow-100' }
  ];

  // Subscribe to Firebase users
  useEffect(() => {
    console.log('⚙️ Settings: Setting up Firebase users sync...');
    setFirebaseUsersLoading(true);
    
    const unsubscribe = subscribeToUsers((firebaseUsers) => {
      console.log('⚙️ Settings: Received', firebaseUsers.length, 'users from Firebase');
      setUsers(firebaseUsers as UserManagement[]);
      setFirebaseUsersLoading(false);
    }, (error) => {
      console.error('⚙️ Settings: Firebase users subscription error:', error);
      setFirebaseUsersLoading(false);
    });

    return () => {
      console.log('⚙️ Settings: Cleaning up Firebase users subscription...');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await updateProfile(profileData.name);
      setMessage('Profile updated successfully!');
    } catch (error: any) {
      setMessage('Failed to update profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Save users to localStorage
  const saveUsers = (updatedUsers: UserManagement[]) => {
    setUsers(updatedUsers);
    localStorage.setItem('recruitment-users', JSON.stringify(updatedUsers));
    
    // Trigger global settings update event
    window.dispatchEvent(new CustomEvent('settingsUpdated', { 
      detail: { users: updatedUsers } 
    }));
  };

  // User Management functions
  const resetUserForm = () => {
    setUserFormData({
      name: '',
      email: '',
      role: 'Broker',
      status: 'Active',
      permissions: [],
      phone: '',
      department: '',
      notes: ''
    });
  };

  const handleAddUser = async () => {
    try {
      const newUser = {
        ...userFormData,
        lastLogin: 'Never',
      };
      
      await addUser(newUser);
      setShowAddUserModal(false);
      resetUserForm();
      alert('User added successfully and synced to Firebase!');
    } catch (error) {
      console.error('Failed to add user:', error);
      alert('Failed to add user. Please try again.');
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    try {
      await updateUser(selectedUser.id, userFormData);
      setShowEditUserModal(false);
      setSelectedUser(null);
      resetUserForm();
      alert('User updated successfully!');
    } catch (error) {
      console.error('Failed to update user:', error);
      alert('Failed to update user. Please try again.');
    }
  };

  const openEditUserModal = (user: UserManagement) => {
    setSelectedUser(user);
    setUserFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status,
      permissions: user.permissions,
      phone: user.phone || '',
      department: user.department || '',
      notes: user.notes || ''
    });
    setShowEditUserModal(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user? They will be moved to the recycle bin.')) {
      try {
        const userToDelete = users.find((u: UserManagement) => u.id === userId);
        
        if (userToDelete) {
          // Add to recycle bin
          addToRecycleBin({
            type: 'user',
            name: userToDelete.name,
            details: `${userToDelete.role} | Email: ${userToDelete.email} | Status: ${userToDelete.status}`,
            deletedBy: 'Current User',
            data: userToDelete
          });

          // Soft delete from Firebase
          await softDeleteUser(userId);
          alert('User moved to recycle bin!');
        }
      } catch (error) {
        console.error('Failed to delete user:', error);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const handleRoleChange = (userId: string, newRole: string) => {
    const formattedRole = newRole.charAt(0).toUpperCase() + newRole.slice(1);
    const updatedUsers = users.map((u: UserManagement) => 
      u.id === userId ? { ...u, role: formattedRole } : u
    );
    saveUsers(updatedUsers);
    alert(`User role updated to ${formattedRole}`);
  };

  const handleStatusChange = (userId: string, newStatus: string) => {
    const formattedStatus = newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
    const updatedUsers = users.map((u: UserManagement) => 
      u.id === userId ? { ...u, status: formattedStatus } : u
    );
    saveUsers(updatedUsers);
    alert(`User status updated to ${formattedStatus}`);
  };

  const togglePermission = (permission: string) => {
    const updatedPermissions = userFormData.permissions.includes(permission)
      ? userFormData.permissions.filter(p => p !== permission)
      : [...userFormData.permissions, permission];
    setUserFormData({ ...userFormData, permissions: updatedPermissions });
  };

  // Handle settings changes with live sync
  const handleSettingChange = (key: string, value: string) => {
    setTempSettings(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
    
    // Auto-save after a short delay (debounced)
    const timeoutId = setTimeout(() => {
      saveSettings({ [key]: value });
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  };

  // Save settings function
  const saveSettings = async (specificSettings?: Record<string, string>) => {
    setLoading(true);
    try {
      const settingsToSave = specificSettings || tempSettings;
      
      // Update app settings
      updateAppSettings({
        applicationName: settingsToSave.applicationName || tempSettings.applicationName,
        defaultUserRole: settingsToSave.defaultUserRole || tempSettings.defaultUserRole
      });
      
      // Save additional settings to localStorage
      const additionalSettings = {
        dataRetention: settingsToSave.dataRetention || tempSettings.dataRetention,
        defaultCountry: settingsToSave.defaultCountry || tempSettings.defaultCountry,
        defaultCurrency: settingsToSave.defaultCurrency || tempSettings.defaultCurrency,
        autoLogout: settingsToSave.autoLogout || tempSettings.autoLogout
      };
      
      localStorage.setItem('recruitment-additional-settings', JSON.stringify(additionalSettings));
      
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      setMessage('Settings saved successfully!');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
      
      // Trigger a custom event to notify other components of settings change
      window.dispatchEvent(new CustomEvent('settingsUpdated', { 
        detail: { ...settingsToSave, ...additionalSettings } 
      }));
      
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save when component unmounts
  React.useEffect(() => {
    return () => {
      if (hasUnsavedChanges) {
        saveSettings();
      }
    };
  }, [hasUnsavedChanges]);

  // Load additional settings on mount
  React.useEffect(() => {
    const additionalSettings = localStorage.getItem('recruitment-additional-settings');
    if (additionalSettings) {
      const parsed = JSON.parse(additionalSettings);
      setTempSettings(prev => ({ ...prev, ...parsed }));
    }
  }, []);

  // Listen for settings changes from other parts of the app
  React.useEffect(() => {
    const handleSettingsUpdate = (event: CustomEvent) => {
      setMessage('Settings synchronized across the application');
      setTimeout(() => setMessage(''), 3000);
    };

    window.addEventListener('settingsUpdated', handleSettingsUpdate as EventListener);
    
    // Also listen for storage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'recruitment-settings' || e.key === 'recruitment-additional-settings') {
        setMessage('Settings updated from another tab');
        setTimeout(() => setMessage(''), 3000);
        
        // Reload settings
        if (e.key === 'recruitment-additional-settings' && e.newValue) {
          const parsed = JSON.parse(e.newValue);
          setTempSettings(prev => ({ ...prev, ...parsed }));
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsUpdate as EventListener);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>Settings</h1>
        <p className="text-gray-600 mt-1">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Save Settings Area */}
      <div className={`${recruitmentTheme.components.card} bg-gradient-to-r from-blue-50 to-indigo-50 border-2 ${hasUnsavedChanges ? 'border-orange-300' : 'border-green-300'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${hasUnsavedChanges ? 'bg-orange-500 animate-pulse' : 'bg-green-500'}`}></div>
            <div>
              <h3 className="font-semibold text-[#8b6914]">
                {hasUnsavedChanges ? 'Changes Pending' : 'All Changes Saved'}
              </h3>
              <p className="text-sm text-gray-600">
                {hasUnsavedChanges 
                  ? 'Auto-saving in progress...' 
                  : lastSaved 
                    ? `Last saved: ${lastSaved.toLocaleString()}`
                    : 'All settings are synchronized'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {message && (
              <span className={`text-sm px-3 py-1 rounded-full ${
                message.includes('success') 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {message}
              </span>
            )}
            <button
              onClick={() => saveSettings()}
              disabled={!hasUnsavedChanges || loading}
              className={`${
                hasUnsavedChanges 
                  ? recruitmentTheme.components.button.primary 
                  : recruitmentTheme.components.button.secondary
              } flex items-center space-x-2`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{hasUnsavedChanges ? 'Save Now' : 'Saved'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* User Management Section */}
      <div className={recruitmentTheme.components.card}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-[#8b6914]">
                <Users className="w-5 h-5 text-[#8b6914]" />
                User Management
                <button 
                  onClick={() => setShowAddUserModal(true)}
                  className="bg-white hover:bg-gray-50 text-[#8b6914] border-2 border-[#f0e4cc] rounded-xl px-6 py-3 font-semibold transition-all duration-200 flex items-center space-x-2 ml-4"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add User</span>
                </button>
              </CardTitle>
              <CardDescription className="text-gray-600">Manage users, roles, and permissions</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-[#8b6914]">User</th>
                  <th className="text-left py-3 px-4 font-medium text-[#8b6914]">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-[#8b6914]">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-[#8b6914]">Last Login</th>
                  <th className="text-left py-3 px-4 font-medium text-[#8b6914]">Permissions</th>
                  <th className="text-left py-3 px-4 font-medium text-[#8b6914]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                          {index + 1}.
                        </span>
                        <div>
                          <div className="font-medium text-[#8b6914]">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge
                        currentStatus={user.role.toLowerCase()}
                        options={userRoleOptions}
                        onStatusChange={(newRole) => handleRoleChange(user.id, newRole)}
                        size="sm"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge
                        currentStatus={user.status.toLowerCase()}
                        options={userStatusOptions}
                        onStatusChange={(newStatus) => handleStatusChange(user.id, newStatus)}
                        size="sm"
                      />
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{user.lastLogin}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {user.permissions.map((permission: string) => (
                          <span key={permission} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {permission}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => openEditUserModal(user)}
                          className={`${recruitmentTheme.components.button.ghost} text-blue-600 hover:text-blue-700`}
                          title="Edit user"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteUser(user.id)}
                          className={`${recruitmentTheme.components.button.ghost} text-red-600 hover:text-red-700`}
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className={recruitmentTheme.components.card}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#8b6914]">
                <User className="w-5 h-5 text-[#8b6914]" />
                Profile Information
              </CardTitle>
              <CardDescription className="text-gray-600">Update your personal information and details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                {message && (
                  <div className={`p-3 rounded ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {message}
                  </div>
                )}

                <div>
                  <label htmlFor="name" className={recruitmentTheme.components.form.label}>
                    Full Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    className={`${recruitmentTheme.components.input} mt-1`}
                  />
                </div>

                <div>
                  <label htmlFor="email" className={recruitmentTheme.components.form.label}>
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={profileData.email}
                    disabled
                    className={`${recruitmentTheme.components.input} mt-1 bg-gray-50`}
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <label className={recruitmentTheme.components.form.label}>
                    Role
                  </label>
                  <input
                    type="text"
                    value={user?.role || ''}
                    disabled
                    className={`${recruitmentTheme.components.input} mt-1 bg-gray-50`}
                  />
                  <p className="text-xs text-gray-500 mt-1">Role is assigned by administrator</p>
                </div>

                <button type="submit" disabled={loading} className={recruitmentTheme.components.button.primary}>
                  {loading ? 'Updating...' : 'Update Profile'}
                </button>
              </form>
            </CardContent>
          </div>

          <div className={recruitmentTheme.components.card}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#8b6914]">
                <Shield className="w-5 h-5 text-[#8b6914]" />
                Security Settings
              </CardTitle>
              <CardDescription className="text-gray-600">Manage your password and security preferences</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-[#8b6914] dark:text-[#FFD700]">Change Password</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Update your password to keep your account secure</p>
                  <button 
                    onClick={() => setShowPasswordModal(true)}
                    className={recruitmentTheme.components.button.secondary}
                  >
                    Change Password
                  </button>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium text-[#8b6914]">Two-Factor Authentication</h4>
                  <p className="text-sm text-gray-500 mb-3">Add an extra layer of security to your account</p>
                  <button disabled className={recruitmentTheme.components.button.secondary}>Enable 2FA (Coming Soon)</button>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium text-[#8b6914]">Session Management</h4>
                  <p className="text-sm text-gray-500 mb-3">Manage your active sessions and login security</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Auto-logout after inactivity</span>
                      <select 
                        value={tempSettings.autoLogout}
                        onChange={(e) => handleSettingChange('autoLogout', e.target.value)}
                        className={`${recruitmentTheme.components.select} text-xs`}
                      >
                        <option value="15">15 minutes</option>
                        <option value="30">30 minutes</option>
                        <option value="60">1 hour</option>
                        <option value="120">2 hours</option>
                        <option value="0">Never</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </div>

          <div className={recruitmentTheme.components.card}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#8b6914]">
                <Bell className="w-5 h-5 text-[#8b6914]" />
                Notification Preferences
              </CardTitle>
              <CardDescription className="text-gray-600">Choose how you want to be notified</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-[#8b6914] dark:text-[#FFD700]">Email Notifications</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications via email</p>
                  </div>
                  <input 
                    type="checkbox" 
                    className="rounded accent-[#b8860b] w-4 h-4" 
                    checked={settings.notifications.emailNotifications}
                    onChange={(e) => {
                      updateNotificationSettings({ emailNotifications: e.target.checked });
                      setMessage(`Email notifications ${e.target.checked ? 'enabled' : 'disabled'}`);
                      setTimeout(() => setMessage(''), 2000);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-[#8b6914] dark:text-[#FFD700]">WhatsApp Notifications</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications via WhatsApp</p>
                  </div>
                  <input 
                    type="checkbox" 
                    className="rounded accent-[#b8860b] w-4 h-4" 
                    checked={settings.notifications.whatsappNotifications}
                    onChange={(e) => {
                      updateNotificationSettings({ whatsappNotifications: e.target.checked });
                      setMessage(`WhatsApp notifications ${e.target.checked ? 'enabled' : 'disabled'}`);
                      setTimeout(() => setMessage(''), 2000);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-[#8b6914] dark:text-[#FFD700]">Task Reminders</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Get reminded about upcoming tasks</p>
                  </div>
                  <input 
                    type="checkbox" 
                    className="rounded accent-[#b8860b] w-4 h-4" 
                    checked={settings.notifications.taskReminders}
                    onChange={(e) => {
                      updateNotificationSettings({ taskReminders: e.target.checked });
                      setMessage(`Task reminders ${e.target.checked ? 'enabled' : 'disabled'}`);
                      setTimeout(() => setMessage(''), 2000);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-[#8b6914] dark:text-[#FFD700]">Candidate Updates</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Notifications when candidate status changes</p>
                  </div>
                  <input 
                    type="checkbox" 
                    className="rounded accent-[#b8860b] w-4 h-4" 
                    checked={settings.notifications.candidateUpdates}
                    onChange={(e) => {
                      updateNotificationSettings({ candidateUpdates: e.target.checked });
                      setMessage(`Candidate updates ${e.target.checked ? 'enabled' : 'disabled'}`);
                      setTimeout(() => setMessage(''), 2000);
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </div>
        </div>

        <div className="space-y-6">
          <div className={recruitmentTheme.components.card}>
            <CardHeader>
              <CardTitle className="text-[#8b6914]">Application Settings</CardTitle>
              <CardDescription className="text-gray-600">General application preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className={`${recruitmentTheme.components.form.label} mb-2`}>
                  Application Name
                </label>
                <input 
                  value={tempSettings.applicationName} 
                  onChange={(e) => handleSettingChange('applicationName', e.target.value)}
                  className={`${recruitmentTheme.components.input} dark:bg-gray-700 dark:text-white dark:border-gray-600`} 
                  placeholder="Recruitment CRM"
                />
              </div>

              <div>
                <label className={`${recruitmentTheme.components.form.label} mb-2`}>
                  Default User Role
                </label>
                <select 
                  value={tempSettings.defaultUserRole} 
                  onChange={(e) => handleSettingChange('defaultUserRole', e.target.value)}
                  className={`${recruitmentTheme.components.select} dark:bg-gray-700 dark:text-white dark:border-gray-600`}
                >
                  <option value="Broker">Broker</option>
                  <option value="Receptionist">Receptionist</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>

              <div>
                <label className={`${recruitmentTheme.components.form.label} mb-2`}>
                  Data Retention (Days)
                </label>
                <select 
                  value={tempSettings.dataRetention}
                  onChange={(e) => handleSettingChange('dataRetention', e.target.value)}
                  className={`${recruitmentTheme.components.select} dark:bg-gray-700 dark:text-white dark:border-gray-600`}
                >
                  <option value="30">30 Days</option>
                  <option value="90">90 Days</option>
                  <option value="180">6 Months</option>
                  <option value="365">1 Year</option>
                  <option value="730">2 Years</option>
                  <option value="0">Never Delete</option>
                </select>
              </div>

              <div>
                <label className={`${recruitmentTheme.components.form.label} mb-2`}>
                  Default Country
                </label>
                <select 
                  value={tempSettings.defaultCountry}
                  onChange={(e) => handleSettingChange('defaultCountry', e.target.value)}
                  className={`${recruitmentTheme.components.select} dark:bg-gray-700 dark:text-white dark:border-gray-600`}
                >
                  <option value="Kenya">Kenya</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="UAE">UAE</option>
                  <option value="Kuwait">Kuwait</option>
                  <option value="Qatar">Qatar</option>
                </select>
              </div>

              <div>
                <label className={`${recruitmentTheme.components.form.label} mb-2`}>
                  Default Currency
                </label>
                <select 
                  value={tempSettings.defaultCurrency}
                  onChange={(e) => handleSettingChange('defaultCurrency', e.target.value)}
                  className={`${recruitmentTheme.components.select} dark:bg-gray-700 dark:text-white dark:border-gray-600`}
                >
                  <option value="KES">Kenyan Shilling (KES)</option>
                  <option value="SAR">Saudi Riyal (SAR)</option>
                  <option value="AED">UAE Dirham (AED)</option>
                  <option value="KWD">Kuwaiti Dinar (KWD)</option>
                  <option value="QAR">Qatari Riyal (QAR)</option>
                  <option value="USD">US Dollar (USD)</option>
                </select>
              </div>
            </CardContent>
          </div>

          <div className={recruitmentTheme.components.card}>
            <CardHeader>
              <CardTitle className="text-[#8b6914]">Account Information</CardTitle>
              <CardDescription className="text-gray-600">Your account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <span className="text-sm text-gray-500">User ID</span>
                <p className="font-mono text-sm">{user?.id}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Last Login</span>
                <p className="text-sm">{user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Account Status</span>
                <p className="text-sm text-green-600">Active</p>
              </div>
            </CardContent>
          </div>

          <div className={recruitmentTheme.components.card}>
            <CardHeader>
              <CardTitle className="text-[#8b6914]">Settings Management</CardTitle>
              <CardDescription className="text-gray-600">Backup and restore your settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-[#8b6914]">Settings Sync</h4>
                  <p className="text-sm text-gray-500">Real-time synchronization across all components</p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-600">Live</span>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      const settingsBackup = {
                        ...settings,
                        additionalSettings: tempSettings,
                        exportDate: new Date().toISOString(),
                        version: '1.0'
                      };
                      const dataStr = JSON.stringify(settingsBackup, null, 2);
                      const dataBlob = new Blob([dataStr], { type: 'application/json' });
                      const url = URL.createObjectURL(dataBlob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `recruitment-crm-settings-${new Date().toISOString().split('T')[0]}.json`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                      alert('Settings exported successfully!');
                    }}
                    className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2`}
                  >
                    <Download className="w-4 h-4" />
                    <span>Export Settings</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.json';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (e) => {
                            try {
                              const importedSettings = JSON.parse(e.target?.result as string);
                              if (importedSettings.additionalSettings) {
                                setTempSettings(importedSettings.additionalSettings);
                                saveSettings(importedSettings.additionalSettings);
                                alert('Settings imported successfully!');
                              }
                            } catch (error) {
                              alert('Invalid settings file format.');
                            }
                          };
                          reader.readAsText(file);
                        }
                      };
                      input.click();
                    }}
                    className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2`}
                  >
                    <Upload className="w-4 h-4" />
                    <span>Import Settings</span>
                  </button>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
                      // Reset to defaults
                      const defaultTempSettings = {
                        applicationName: 'Recruitment CRM',
                        defaultUserRole: 'Broker',
                        dataRetention: '365',
                        defaultCountry: 'Kenya',
                        defaultCurrency: 'KES',
                        autoLogout: '30'
                      };
                      setTempSettings(defaultTempSettings);
                      saveSettings(defaultTempSettings);
                      alert('Settings reset to defaults successfully!');
                    }
                  }}
                  className={`${recruitmentTheme.components.button.secondary} text-red-600 hover:text-red-700 flex items-center space-x-2`}
                >
                  <Settings className="w-4 h-4" />
                  <span>Reset to Defaults</span>
                </button>
              </div>
            </CardContent>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#8b6914]">Add New User</h2>
              <button 
                onClick={() => { setShowAddUserModal(false); resetUserForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Full Name</label>
                <input
                  type="text"
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({...userFormData, name: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Optional"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Email Address</label>
                <input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Optional"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={userFormData.phone}
                  onChange={(e) => setUserFormData({...userFormData, phone: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Optional"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Department</label>
                <input
                  type="text"
                  value={userFormData.department}
                  onChange={(e) => setUserFormData({...userFormData, department: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Optional"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Role</label>
                <select
                  value={userFormData.role}
                  onChange={(e) => setUserFormData({...userFormData, role: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="Broker">Broker</option>
                  <option value="Receptionist">Receptionist</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Status</label>
                <select
                  value={userFormData.status}
                  onChange={(e) => setUserFormData({...userFormData, status: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Additional Notes</label>
                <textarea
                  value={userFormData.notes}
                  onChange={(e) => setUserFormData({...userFormData, notes: e.target.value})}
                  className={`${recruitmentTheme.components.textarea} h-20`}
                  placeholder="Optional notes about the user..."
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-3">Page Access Permissions</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availablePermissions.map((permission) => (
                    <label key={permission.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userFormData.permissions.includes(permission.value)}
                        onChange={() => togglePermission(permission.value)}
                        className="rounded border-gray-300 text-[#8b6914] focus:ring-[#8b6914]"
                      />
                      <span className="text-sm text-gray-700">{permission.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Select which pages this user can access</p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => { setShowAddUserModal(false); resetUserForm(); }}
                className={recruitmentTheme.components.button.secondary}
              >
                Cancel
              </button>
              <button 
                onClick={handleAddUser}
                className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2`}
              >
                <Save className="w-4 h-4" />
                <span>Add User</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#8b6914]">Edit User</h2>
              <button 
                onClick={() => { 
                  setShowEditUserModal(false); 
                  setSelectedUser(null);
                  resetUserForm(); 
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Full Name</label>
                <input
                  type="text"
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({...userFormData, name: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Email Address</label>
                <input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={userFormData.phone}
                  onChange={(e) => setUserFormData({...userFormData, phone: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Department</label>
                <input
                  type="text"
                  value={userFormData.department}
                  onChange={(e) => setUserFormData({...userFormData, department: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Role</label>
                <select
                  value={userFormData.role}
                  onChange={(e) => setUserFormData({...userFormData, role: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="Broker">Broker</option>
                  <option value="Receptionist">Receptionist</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Status</label>
                <select
                  value={userFormData.status}
                  onChange={(e) => setUserFormData({...userFormData, status: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Additional Notes</label>
                <textarea
                  value={userFormData.notes}
                  onChange={(e) => setUserFormData({...userFormData, notes: e.target.value})}
                  className={`${recruitmentTheme.components.textarea} h-20`}
                  placeholder="Additional notes about the user..."
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-3">Page Access Permissions</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availablePermissions.map((permission) => (
                    <label key={permission.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userFormData.permissions.includes(permission.value)}
                        onChange={() => togglePermission(permission.value)}
                        className="rounded border-gray-300 text-[#8b6914] focus:ring-[#8b6914]"
                      />
                      <span className="text-sm text-gray-700">{permission.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Select which pages this user can access</p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => { 
                  setShowEditUserModal(false); 
                  setSelectedUser(null);
                  resetUserForm(); 
                }}
                className={recruitmentTheme.components.button.secondary}
              >
                Cancel
              </button>
              <button 
                onClick={handleEditUser}
                className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2`}
              >
                <Save className="w-4 h-4" />
                <span>Update User</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      <ChangePasswordModal 
        isOpen={showPasswordModal} 
        onClose={() => setShowPasswordModal(false)}
        onSubmit={async (currentPassword: string, newPassword: string) => {
          // TODO: Implement password change logic
          console.log('Password change requested');
          setShowPasswordModal(false);
        }}
      />
    </div>
  );
};

export default SettingsPage;